// Add or edit a single measurement. Metric input with a stepper + free entry;
// always asks for a date (defaults to today) so historical data is easy.

import { useEffect, useState } from 'react';
import type { Measurement } from '@shared/types.ts';
import type { MeasurementKind } from '@shared/constants.ts';
import { MEASUREMENT_KINDS } from '@shared/constants.ts';
import { Icon } from '../Icon.tsx';
import { SheetShell, SegRow } from './SheetShell.tsx';
import {
  ConfirmDeletePanel,
  DateWithOptionalTime,
  DescriptionField,
  noonTodayLocal,
  timeIsSentinel,
  useConfirmDelete,
} from './fields.tsx';
import { KIND_META } from '../../lib/measure.ts';

type Props = {
  open: boolean;
  onClose: () => void;
  defaultKind?: MeasurementKind;
  /** Restrict the "What" picker to a subset of kinds. When the array has one
   *  entry the picker is hidden entirely. */
  kinds?: readonly MeasurementKind[];
  existing?: Measurement | null;
  onSubmit: (vars: {
    kind: MeasurementKind;
    value: number;
    occurred_at: number;
    notes?: string | null;
  }) => Promise<unknown> | void;
  onDelete?: () => Promise<unknown> | void;
};

export function MeasurementSheet({
  open,
  onClose,
  defaultKind = 'weight',
  kinds,
  existing,
  onSubmit,
  onDelete,
}: Props) {
  const allowedKinds = kinds ?? MEASUREMENT_KINDS;
  const [kind, setKind] = useState<MeasurementKind>(existing?.kind ?? defaultKind);
  const [displayValue, setDisplayValue] = useState<string>('');
  const [occurredAt, setOccurredAt] = useState<number>(
    existing?.occurred_at ?? defaultOccurredAt(existing?.kind ?? defaultKind),
  );
  const [notes, setNotes] = useState<string>(existing?.notes ?? '');
  const [showTime, setShowTime] = useState<boolean>(() =>
    existing
      ? !timeIsSentinel(existing.occurred_at)
      : KIND_META[defaultKind].defaultShowTime,
  );
  const confirm = useConfirmDelete();

  useEffect(() => {
    if (!open) return;
    const k = existing?.kind ?? defaultKind;
    setKind(k);
    if (existing) {
      const meta = KIND_META[existing.kind];
      const mult = 10 ** meta.precision;
      setDisplayValue((Math.round(meta.toDisplay(existing.value) * mult) / mult).toFixed(meta.precision));
      setOccurredAt(existing.occurred_at);
      setNotes(existing.notes ?? '');
      setShowTime(!timeIsSentinel(existing.occurred_at));
    } else {
      setDisplayValue('');
      setOccurredAt(defaultOccurredAt(k));
      setNotes('');
      setShowTime(KIND_META[k].defaultShowTime);
    }
    confirm.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, existing?.id]);

  // When the user toggles the "What" picker, reset value + time defaults so
  // a temperature switch doesn't keep a kg value or noon timestamp around.
  useEffect(() => {
    if (existing) return;
    setDisplayValue('');
    setOccurredAt(defaultOccurredAt(kind));
    setShowTime(KIND_META[kind].defaultShowTime);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind]);

  const meta = KIND_META[kind];
  const parsedNum = Number(displayValue.replace(',', '.'));
  const valid = Number.isFinite(parsedNum) && parsedNum > 0;

  return (
    <SheetShell
      open={open}
      onClose={onClose}
      title={existing ? `Edit ${meta.label.toLowerCase()}` : `Add ${meta.label.toLowerCase()}`}
      tagline=""
      accent={meta.accent}
      icon={(p) => <Icon.chart {...p} />}
      primaryLabel={confirm.confirming ? 'Yes, delete' : 'Save'}
      primaryDanger={confirm.confirming}
      primaryDisabled={!confirm.confirming && !valid}
      secondaryLabel={confirm.confirming ? 'Keep' : 'Cancel'}
      onPrimary={async () => {
        if (confirm.confirming && onDelete) {
          await onDelete();
          confirm.reset();
          onClose();
          return;
        }
        if (!valid) return;
        await onSubmit({
          kind,
          value: meta.fromDisplay(parsedNum),
          occurred_at: occurredAt,
          notes: notes.trim() || null,
        });
        onClose();
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!existing && allowedKinds.length > 1 && (
          <SegRow
            label="What"
            active={kind}
            onChange={setKind}
            options={allowedKinds.map((id) => ({ id, label: KIND_META[id].label }))}
          />
        )}

        <div
          style={{
            padding: '16px 18px',
            borderRadius: 16,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
            }}
          >
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.04,
              }}
            >
              {meta.label}
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{meta.unit}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
            <input
              inputMode="decimal"
              pattern="[0-9.,]*"
              placeholder={meta.placeholder}
              value={displayValue}
              onChange={(e) => {
                // Keep just digits + a single decimal separator
                const cleaned = e.target.value.replace(/[^0-9.,]/g, '').replace(/[.,]+/g, '.');
                setDisplayValue(cleaned);
              }}
              className="lt-serif lt-tabular"
              style={{
                flex: 1,
                fontSize: 38,
                fontWeight: 400,
                letterSpacing: -0.02,
                color: 'var(--text-1)',
                padding: 0,
              }}
            />
            <span style={{ fontSize: 16, color: 'var(--text-3)' }}>{meta.unit}</span>
          </div>
        </div>

        <DateWithOptionalTime
          occurredAt={occurredAt}
          onChange={setOccurredAt}
          showTime={showTime}
          onToggleTime={setShowTime}
        />

        <DescriptionField value={notes} onChange={setNotes} placeholder={meta.notesPlaceholder} />

        {onDelete && !confirm.confirming && (
          <button
            type="button"
            className="lt-btn lt-btn-ghost"
            style={{ width: '100%', padding: '10px 0', fontSize: 13, color: 'var(--danger)' }}
            onClick={confirm.startConfirm}
          >
            <Icon.trash s={14} /> Delete this measurement
          </button>
        )}
        {confirm.confirming && (
          <ConfirmDeletePanel message="This is permanent. The measurement will disappear from the graph." />
        )}
      </div>
    </SheetShell>
  );
}

/** Growth measurements default to noon (date matters, time doesn't);
 *  temperature wants the actual moment of the reading. */
function defaultOccurredAt(k: MeasurementKind): number {
  return KIND_META[k].defaultShowTime ? Math.floor(Date.now() / 1000) : noonTodayLocal();
}
